import {
	Link,
	useLoaderData,
	useLocation,
	useNavigate,
	useNavigation,
} from '@remix-run/react'
import { motion } from 'framer-motion'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import LoadingSection from '#app/components/common/loading-page'
import { TOTAL_ITEMS_PER_PAGE } from '#app/constants/clerk-api'
import { useUtmParams } from '#app/hooks/useUtmParams'
import { type loader } from '#app/routes/_public+/products+/$slug'
import { useClerkVisitorStore } from '#app/store/clerk-visitor'
import { getLokalisedString } from '#app/utils/localisation/lokalisation-utilities'
import { cn } from '#app/utils/misc'
import { trackPLPFilterChange } from '#app/utils/tracking/amplitude/plp-change-filter-sorting'
import { trackProductCardClick } from '#app/utils/tracking/amplitude/plp-click'
import { trackPLPView } from '#app/utils/tracking/amplitude/plp-viewed'
import { logClickEvent } from '#app/utils/tracking/clerk-tracking'
import { type ClerkProduct } from '#types/product'
import ErrorPage from '../checkout/components/error-page'
import ClerkProductCard from './clerk-components/clerk-product-card/clerk-product-card'
import FilterAndSortSheet from './clerk-components/filters/mobile-v2/filter-and-sort-sheet'
import Pagination from './clerk-components/pagination'
import ProductListFilters from './clerk-components/plp-filters'
import StoreSelector from './clerk-components/store-selector'
import MoreProducts from './clerk-components/store-selector/more-products-banner'
import NoProductsFound from './clerk-components/store-selector/no-products-found'
import ProductsListHeader from './plp-header'
import { MainCategoryHeader } from './plp-main-category-header'

const mainCategories = ['women', 'men', 'kids']

const categoryNames: Record<string, { 'da-DK': string; en: string }> = {
	women: { 'da-DK': 'Kvinder', en: 'Women' },
	men: { 'da-DK': 'Mænd', en: 'Men' },
	kids: { 'da-DK': 'Børn', en: 'Kids' },
}

const ProductListItemsClerk = () => {
	const location = useLocation()
	const data = useLoaderData<typeof loader>()
	const { i18n } = useTranslation() // Get current language from i18n
	const utmParams = useUtmParams() || {}! // Default to empty object if null
	const currentLocale = i18n.language

	const pathParts = location.pathname.split('/')
	const categoryKey = pathParts[2] || ''
	const currentPage = parseInt(
		new URLSearchParams(location.search).get('p') || '1',
	)
	const productsPerPage = TOTAL_ITEMS_PER_PAGE
	const navigate = useNavigate()
	const searchParams = new URLSearchParams(location.search)

	// Correctly calculate totalPages based on the fixed number of products per page
	const totalPages = Math.max(1, Math.ceil(data.totalCount / productsPerPage))

	// Check if currentPage is greater than totalPages and adjust if necessary
	const validatedCurrentPage = Math.min(currentPage, totalPages)

	const isMainCategory = mainCategories.includes(categoryKey)
	const previousPathRef = useRef<string | null>(null)

	useEffect(() => {
		const currentPath = location.pathname
		const currentSearchParams = new URLSearchParams(location.search)
		const previousSearchParams = new URLSearchParams(
			previousPathRef.current?.split('?')[1] || '',
		)

		const searchParamsChanged = (param: string) =>
			currentSearchParams.get(param) !== previousSearchParams.get(param)

		const isStorePagePLP = currentSearchParams.has('store')

		const shouldTrackPLPView =
			!previousPathRef.current ||
			currentPath !== previousPathRef.current.split('?')[0] ||
			isStorePagePLP

		const shouldTrackFilterChange = [
			'ageGroup',
			'brand',
			'color',
			'gender',
			'size',
			'materials',
			'sortingOrder',
		].some(searchParamsChanged)

		if (shouldTrackPLPView) {
			trackPLPView(location, utmParams)
			previousPathRef.current = location.pathname + location.search
		} else if (shouldTrackFilterChange) {
			trackPLPFilterChange(location)
			previousPathRef.current = location.pathname + location.search
		}
	}, [location, utmParams])

	const handleNextPage = () => {
		searchParams.set('p', (validatedCurrentPage + 1).toString())
		navigate(`?${searchParams.toString()}`)
	}

	const handlePreviousPage = () => {
		searchParams.set('p', (validatedCurrentPage - 1).toString())
		navigate(`?${searchParams.toString()}`)
	}
	const localizedTitle = getLokalisedString(
		categoryNames[categoryKey.split('-')[0]],
		currentLocale,
	)
	if (data.error) {
		return <ErrorPage />
	}

	return (
		<>
			<div>
				<MainCategoryHeader
					isMainCategory={isMainCategory}
					localizedTitle={localizedTitle}
				/>
				<StoreSelector />
			</div>
			<div className="m-0 mx-auto flex w-full flex-col justify-center px-4 md:px-[var(--plenty-padding)]">
				<ProductsListHeader />
			</div>
			<FilterAndSortSheet />
			<div className="m-0 mx-auto flex w-full flex-col justify-center px-4 md:px-[var(--plenty-padding)]">
				<ProductListFilters />
				<NoProductsFound />
				<motion.div
					className="grid grid-cols-2 gap-x-2 gap-y-4 pt-2 md:grid-cols-4 md:gap-x-4 md:gap-y-6 md:pt-6"
					data-testid="plp-product-list"
				>
					{data.productsData?.product_data ? (
						<ProductList
							filteredProducts={data.productsData.product_data}
							utmParams={utmParams}
						/>
					) : (
						<div>
							dwadwa
							<LoadingSection />
						</div>
					)}
				</motion.div>
			</div>
			<Pagination
				currentPage={validatedCurrentPage}
				totalPages={totalPages}
				onNextPage={handleNextPage}
				onPreviousPage={handlePreviousPage}
			/>
			<MoreProducts />
		</>
	)
}

const ProductList = ({
	filteredProducts,
	utmParams,
}: {
	filteredProducts: any
	utmParams: any
}) => {
	const { clerkVisitor } = useClerkVisitorStore()

	const location = useLocation()

	const navigation = useNavigation()
	const getStoresParams = () => {
		const stores = new URLSearchParams(location.search).getAll('store')

		return stores.length ? `&store=${stores.join('&store=')}` : ''
	}

	const handleProductClick = (
		e: React.MouseEvent,
		productId: string,
		index: number,
		product: any,
	) => {
		logClickEvent(productId, index, clerkVisitor!)
		trackProductCardClick(product, index, location, utmParams)
	}
	return (
		<>
			{filteredProducts.map((product: ClerkProduct, index: number) => (
				<motion.div
					layout
					layoutId={product.style_id}
					key={product.style_id}
					initial={{ opacity: 0, y: 50 }}
					animate={{ opacity: 1, y: 0 }}
					exit={{ opacity: 0, y: 50 }}
					className={cn(navigation.state === 'idle' ? '' : 'animate-pulse')}
				>
					<Link
						to={`/product/${product.style_id}${getStoresParams()}`}
						state={{
							previousType: location.pathname.includes('group')
								? 'group'
								: 'product',
							previousURL: location.pathname.split('/')[2] || null,
							amplitudePageSource: location.pathname, // Pass the full URL
						}}
						data-testid={`product-card-${index}`}
						onClick={e => handleProductClick(e, product.id, index, product)}
						unstable_viewTransition={true}
					>
						<ClerkProductCard
							product={product}
							variants={filteredProducts.filter(
								(p: { stylenumber: any; style_id: any }) =>
									p.stylenumber === product.stylenumber &&
									p.style_id !== product.style_id,
							)}
						/>
					</Link>
				</motion.div>
			))}
		</>
	)
}

export default ProductListItemsClerk
